// @vendors
const Immutable = require('immutable');
const trim = require('lodash/string/trim');
const map = require('lodash/collection/map');
const get = require('lodash/object/get');
const moment = require('moment');

// @constants
const actionTypes = require('constants/actionTypes');
const { DATE_FORMAT_API2 } = require('constants/index');
const { BROKER_CONVENIENCE_MIFID_TEST_ADD_PRIVATE_EQUITY_DISCLAIMERS } = require('mifid/constants/actionTypes');
const MIFID_APPROVED_RESULT = 'A';
const MIFID_REJECT_RESULT = 'R';

const INITIAL_STATE = Immutable.fromJS({
    firstTimePerformingTest: false,
    isConvenient: false,
    score: 0,
    scoring: 0,
    scoreText: "",
    targetMarketKnowledgeAndExperience: "",
    result: "",
    resultText: "",
    requiresTest: false,
    usPersonOperationCode: null,
    getTestOperationCode: null,
    operationCodeWasUse: false,
    isinId: "",
    marketId: undefined,
    lastExecutionDate: "",
    convenienceEvaluationDisclaimer: "",
    primaryDisclaimer: "",
    phraseToTranscribe: "",
    usPersonDisclaimers: Immutable.fromJS([]),
    disclaimers: Immutable.fromJS([]),
    objectiveTargetDisclaimers: Immutable.fromJS([]),
    concentrationDisclaimers: Immutable.fromJS([]),
    cryptoDisclaimer: Immutable.fromJS([]),
    warrantDisclaimer: "",
    feeNumbers: undefined,
    valueCode: undefined,
    eventCode: undefined,
    expirationYears: "",
});

function buildDisclaimer(disclaimer) {
    return {
        description: trim(get(disclaimer, 'description')),
        id: trim(get(disclaimer, 'id')),
        phrase: trim(get(disclaimer, 'phrase'))
    };
}

function isObjectiveTargetDisclaimer(disclaimer) {
    return trim(get(disclaimer, 'id')).startsWith('TM');
}

function isCryptoAssetsDisclaimer(disclaimer) {
    return trim(get(disclaimer, "id")).startsWith('CRP');
}

function isWarrantDisclaimer(disclaimer) {
    return trim(get(disclaimer, 'id')).startsWith('W');
}

function isConcentrationDisclaimer(disclaimer) {
    if (__CONTACT_CENTER__) {
        return !isCryptoAssetsDisclaimer(disclaimer) && !isObjectiveTargetDisclaimer(disclaimer) && !isWarrantDisclaimer(disclaimer);
    }else{
        return !isCryptoAssetsDisclaimer(disclaimer) && !isObjectiveTargetDisclaimer(disclaimer)
    }
}

function buildDisclaimers(rawDisclaimers) {
    const disclaimers = map(rawDisclaimers, buildDisclaimer);
    const convenienceEvaluationDisclaimer = trim(get(disclaimers[0], 'description'));
    const phraseToTranscribe = trim(get(disclaimers[0], 'phrase'));

    const disclaimersWithoutEvaluation = disclaimers.slice(1);
    const objectiveTargetDisclaimers = disclaimersWithoutEvaluation.filter(isObjectiveTargetDisclaimer);
    const cryptoDisclaimer = disclaimersWithoutEvaluation.filter(isCryptoAssetsDisclaimer);
    const concentrationDisclaimers = disclaimersWithoutEvaluation.filter(isConcentrationDisclaimer);
    return {
        convenienceEvaluationDisclaimer:
            phraseToTranscribe ?
            convenienceEvaluationDisclaimer + ' "' + phraseToTranscribe + '"' :
            convenienceEvaluationDisclaimer,
        phraseToTranscribe,
        disclaimers: Immutable.fromJS(disclaimers),
        objectiveTargetDisclaimers: Immutable.fromJS(objectiveTargetDisclaimers),
        cryptoDisclaimer: Immutable.fromJS(cryptoDisclaimer),
        concentrationDisclaimers: Immutable.fromJS(concentrationDisclaimers)
    };
}

function getTestFetched(immState, response, optionalParams) {
    const allTests = get(response, 'getTestListado');
    const testToEvaluateId = get(response, 'tipoTestEvaluado')
    const testToEvaluate = allTests.find(test => testToEvaluateId === test.tipoTest)
    const disclaimers = buildDisclaimers(get(response, 'disclaimer'));
    const requiresTest = !get(response, 'disclaimer') ||
        !!get(response, 'disclaimer') &&
        !get(response, 'disclaimer').find(d => d.id === 'D0') &&
        !get(response, 'disclaimer').find(d => d.id === 'TM0')
    const targetMarketKnowledgeAndExperience = trim(get(response, 'targetMarketKnowledgeAndExperience'));
    const scoring = Number(get(response, 'scoring')) || 0;
    const score = Number(get(testToEvaluate, 'score.value')) || 0;
    const scoreText = get(testToEvaluate, 'score.description');
    const lastExecutionDate = trim(get(testToEvaluate, 'lastDateTest'));
    const lastExecutionTimestamp = moment(lastExecutionDate, DATE_FORMAT_API2).format('x');
    const primaryDisclaimer = get(response, 'disclaimer') && get(response, 'disclaimer').find((disclaimer) => {
        return ['D0','D1', 'D2', 'D4', 'EC2', 'EC4', 'EC3'].includes(disclaimer.id)
    });
    const warrantDisclaimer = get(response, 'disclaimer') && get(response, 'disclaimer').find((disclaimer) => {
        return ['W1'].includes(disclaimer.id)
    });

    return immState.merge(disclaimers).merge({
        allTests,
        testToEvaluate,
        firstTimePerformingTest: get(testToEvaluate, 'lastDateTest') == null,
        getTestOperationCode: trim(get(response, 'operationCode')),
        primaryDisclaimer: !!primaryDisclaimer && primaryDisclaimer.description,
        warrantDisclaimer: !!warrantDisclaimer && warrantDisclaimer.description,
        operationCodeWasUse: true,
        lastExecutionDate,
        lastExecutionTimestamp: Number(lastExecutionTimestamp),
        isConvenient: !(Number(get(testToEvaluate, 'reason')) != 3),
        requiresTest,
        reason: Number(get(testToEvaluate, 'reason')),
        targetMarketKnowledgeAndExperience,
        scoring,
        score,
        scoreText,
        feeNumbers: get(optionalParams, 'feeNumbers'),
        valueCode: get(optionalParams, 'valueCode'),
        eventCode: get(optionalParams, 'eventCode')
    });
}

function finish(immState, response) {
    const lastExecutionDate = get(response, 'lastDateTest');
    const newReason = get(response, 'newReason')
    const expirationYears = get(response, 'expirationYears');
    const result = trim(get(response, 'result')).toUpperCase();
    const resultText = trim(get(response, 'score.description'));
    const scoreText = trim(get(response, 'score.description'))
    const disclaimers = buildDisclaimers(get(response, 'disclaimer'));
    const targetMarketKnowledgeAndExperience = trim(get(response, 'targetMarketKnowledgeAndExperience'));
    const scoring = Number(get(response, 'scoring')) || 0;
    const score = Number(get(response, 'score')) || 0;
    const isRejecting = result === MIFID_REJECT_RESULT;
    const primaryDisclaimer = get(response, 'disclaimer') && get(response, 'disclaimer').find((disclaimer) => {
        return ["D1", "D2", "D3", "D4", "EC2", "EC3", "EC4"].includes(disclaimer.id);
    });
    const dataToMerge = {
        isConvenient: result === MIFID_APPROVED_RESULT,
        result,
        resultText,
        targetMarketKnowledgeAndExperience,
        scoring,
        expirationYears,
        primaryDisclaimer: !!primaryDisclaimer && primaryDisclaimer.description,
        score: isRejecting ? 0 : score,
        firstTimePerformingTest: !lastExecutionDate,
        lastExecutionDate,
        reason: newReason,
    }
    if (scoreText) {
        dataToMerge.scoreText = scoreText;
    }
    return immState.merge(disclaimers).merge(dataToMerge);
}

function usPersonFetched(immState, response) {
    const disclaimers = map(get(response, 'disclaimers'), buildDisclaimer);
    const marketId = get(response, 'marketId');

    return immState.merge({
        usPersonOperationCode: get(response, 'operationCode'),
        operationCodeWasUse: false,
        isinId: trim(get(response, 'isinId')),
        marketId: marketId == null ? undefined : trim(marketId),
        usPersonDisclaimers: Immutable.fromJS(disclaimers)
    });
}

function brokerConvenienceMiFIDv2Reducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case actionTypes.BROKER_CONVENIENCE_MIFID_TEST_SET_OPERATION_CODE_US_PERSON:
            return usPersonFetched(state, action.payload);
        case actionTypes.BROKER_CONVENIENCE_MIFID_TEST_FETCH_GET_TEST_SUCCESS:
            return getTestFetched(state, action.payload.response, action.payload.optionalParams);
        case actionTypes.BROKER_CONVENIENCE_MIFID_TEST_FINISH:
            return finish(state, action.payload.response);
        case BROKER_CONVENIENCE_MIFID_TEST_ADD_PRIVATE_EQUITY_DISCLAIMERS:
            return state.merge({
                disclaimers: action.payload.disclaimers
            });
        default:
            return state;
    }
}

module.exports = brokerConvenienceMiFIDv2Reducer;
